import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

const SCROLL_THRESHOLD = 400;
const DISMISS_KEY = "sticky-cta-dismissed";

const StickyCTA = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if previously dismissed in this session
    const isDismissed = sessionStorage.getItem(DISMISS_KEY) === "1";
    if (isDismissed) {
      setDismissed(true);
      return;
    }

    const onScroll = () => {
      const scrolled = window.scrollY;
      const viewportH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      // Hide when near the bottom so the footer is fully visible
      const nearBottom = scrolled + viewportH >= docH - 120;
      setVisible(scrolled > SCROLL_THRESHOLD && !nearBottom);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // Check initial position

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
    handleDismiss();
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-primary border-t border-primary/20 shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
        >
          <div className="container py-3 md:py-4">
            <div className="flex items-center gap-3 md:gap-6">
              {/* Text content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-semibold text-primary-foreground leading-tight">
                  {t("sticky_cta.title")}
                </p>
                <p className="hidden md:block text-xs text-primary-foreground/80 mt-0.5 line-clamp-2">
                  {t("sticky_cta.subtitle")}
                </p>
              </div>

              {/* CTA Button */}
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0 text-sm shadow-brand px-4 md:px-6"
                onClick={scrollToPricing}
              >
                <span className="hidden sm:inline">{t("sticky_cta.button")}</span>
                <span className="sm:hidden">{t("cta.button")}</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>

              {/* Dismiss button */}
              <button
                onClick={handleDismiss}
                className="shrink-0 p-1.5 rounded-full text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                aria-label={t("sticky_cta.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyCTA;
